import { lazy, Suspense } from "react";
import { RouterProvider, createBrowserRouter, Navigate } from "react-router-dom";
import Index from "./pages/Index";
import { LanguageProvider } from "./contexts/LanguageContext";
import { RootLayout } from "./components/RootLayout";
import { AdminLayout } from "./components/admin/AdminLayout";

// Lazy-load pages to reduce initial bundle (~449 KiB savings per PageSpeed)
const About = lazy(() => import("./pages/About"));
const Prices = lazy(() => import("./pages/Prices"));
const Montessori = lazy(() => import("./pages/Montessori"));
const Trilingual = lazy(() => import("./pages/Trilingual"));
const EasterCamp = lazy(() => import("./pages/EasterCamp"));
const SummerCamp = lazy(() => import("./pages/SummerCamp"));
const ChristmasCamp = lazy(() => import("./pages/ChristmasCamp"));
const Extracurriculars = lazy(() => import("./pages/Extracurriculars"));
const Feedback = lazy(() => import("./pages/Feedback"));
const Courses = lazy(() => import("./pages/Courses"));
const SchoolCalendar = lazy(() => import("./pages/SchoolCalendar"));
const Birthdays = lazy(() => import("./pages/Birthdays"));
const Services = lazy(() => import("./pages/Services"));
const Admissions = lazy(() => import("./pages/Admissions"));
const EnrollmentConfirmation = lazy(() => import("./pages/EnrollmentConfirmation"));
const OpenHouseRegistration = lazy(() => import("./pages/OpenHouseRegistration"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Renewal = lazy(() => import("./pages/Renewal"));
const RenewalConfirmation = lazy(() => import("./pages/RenewalConfirmation"));
const SummerCampConfirmation = lazy(() => import("./pages/SummerCampConfirmation"));
const Vecinos = lazy(() => import("./pages/Vecinos"));
const Ciencias = lazy(() => import("./pages/Ciencias"));
const Imagenes = lazy(() => import("./pages/Imagenes"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));

// Admin pages
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));

function PageWithSuspense({ Page }: { Page: React.LazyExoticComponent<React.ComponentType<unknown>> }) {
  return (
    <Suspense fallback={<div className="min-h-[60vh] bg-background" aria-hidden />}>
      <Page />
    </Suspense>
  );
}

function App() {
  const router = createBrowserRouter([
    // Admin routes (outside /:lang/ pattern for cleaner URLs)
    {
      path: "/admin",
      element: <AdminLayout />,
      children: [
        { path: "", element: <Navigate to="/admin/login" replace /> },
        { path: "login", element: <PageWithSuspense Page={AdminLogin} /> },
        { path: "dashboard", element: <PageWithSuspense Page={AdminDashboard} /> },
      ],
    },
    // Public routes
    {
      element: <RootLayout />,
      children: [
        { path: "/", element: <Index /> },

        // Landing campaña "Vecinos" (jul-2026) — URL corta para el QR de la carta
        { path: "/vecinos", element: <PageWithSuspense Page={Vecinos} /> },
        { path: "/:lang/vecinos", element: <PageWithSuspense Page={Vecinos} /> },

        { path: "/:lang", element: <Index /> },
        
        // About - Quiénes Somos
        { path: "/:lang/quienes-somos", element: <PageWithSuspense Page={About} /> },
        { path: "/:lang/about", element: <PageWithSuspense Page={About} /> },
        { path: "/:lang/sobre-nos", element: <PageWithSuspense Page={About} /> },
        { path: "/:lang/a-propos", element: <PageWithSuspense Page={About} /> },
        
        // Prices
        { path: "/:lang/precios", element: <PageWithSuspense Page={Prices} /> },
        { path: "/:lang/prices", element: <PageWithSuspense Page={Prices} /> },
        { path: "/:lang/precos", element: <PageWithSuspense Page={Prices} /> },
        { path: "/:lang/tarifs", element: <PageWithSuspense Page={Prices} /> },
        
        // Montessori
        { path: "/:lang/metodologia-montessori", element: <PageWithSuspense Page={Montessori} /> },
        { path: "/:lang/montessori-education", element: <PageWithSuspense Page={Montessori} /> },
        { path: "/:lang/methode-montessori", element: <PageWithSuspense Page={Montessori} /> },
        
        // Gándara en imágenes
        { path: "/:lang/gandara-en-imagenes", element: <PageWithSuspense Page={Imagenes} /> },
        { path: "/:lang/gandara-in-pictures", element: <PageWithSuspense Page={Imagenes} /> },
        { path: "/:lang/gandara-em-imagens", element: <PageWithSuspense Page={Imagenes} /> },
        { path: "/:lang/gandara-en-images", element: <PageWithSuspense Page={Imagenes} /> },

        // Ciencias
        { path: "/:lang/ciencias", element: <PageWithSuspense Page={Ciencias} /> },
        { path: "/:lang/science", element: <PageWithSuspense Page={Ciencias} /> },
        { path: "/:lang/sciences", element: <PageWithSuspense Page={Ciencias} /> },

        // Trilingual
        { path: "/:lang/educacion-trilingue", element: <PageWithSuspense Page={Trilingual} /> },
        { path: "/:lang/trilingual-education", element: <PageWithSuspense Page={Trilingual} /> },
        { path: "/:lang/educacao-trilingue", element: <PageWithSuspense Page={Trilingual} /> },
        { path: "/:lang/education-trilingue", element: <PageWithSuspense Page={Trilingual} /> },
        
        // Open House Registration
        { path: "/:lang/inscripcion-puertas-abiertas", element: <PageWithSuspense Page={OpenHouseRegistration} /> },
        { path: "/:lang/open-house-registration", element: <PageWithSuspense Page={OpenHouseRegistration} /> },
        { path: "/:lang/inscricao-portas-abertas", element: <PageWithSuspense Page={OpenHouseRegistration} /> },
        { path: "/:lang/inscription-portes-ouvertes", element: <PageWithSuspense Page={OpenHouseRegistration} /> },

        // School Calendar
        { path: "/:lang/calendario-escolar", element: <PageWithSuspense Page={SchoolCalendar} /> },
        { path: "/:lang/school-calendar", element: <PageWithSuspense Page={SchoolCalendar} /> },
        { path: "/:lang/calendrier-scolaire", element: <PageWithSuspense Page={SchoolCalendar} /> },
        
        // Extracurriculars
        { path: "/:lang/extraescolares", element: <PageWithSuspense Page={Extracurriculars} /> },
        { path: "/:lang/extracurriculars", element: <PageWithSuspense Page={Extracurriculars} /> },
        { path: "/:lang/extracurriculares", element: <PageWithSuspense Page={Extracurriculars} /> },
        { path: "/:lang/extrascolaires", element: <PageWithSuspense Page={Extracurriculars} /> },
        
        // Services
        { path: "/:lang/servicios", element: <PageWithSuspense Page={Services} /> },
        { path: "/:lang/services", element: <PageWithSuspense Page={Services} /> },
        { path: "/:lang/servicos", element: <PageWithSuspense Page={Services} /> },
        
        // Admissions
        { path: "/:lang/admisiones", element: <PageWithSuspense Page={Admissions} /> },
        { path: "/:lang/admissions", element: <PageWithSuspense Page={Admissions} /> },
        { path: "/:lang/admissoes", element: <PageWithSuspense Page={Admissions} /> },
        
        // Enrollment Confirmation
        { path: "/:lang/confirmacion-preinscripcion", element: <PageWithSuspense Page={EnrollmentConfirmation} /> },
        { path: "/:lang/enrollment-confirmation", element: <PageWithSuspense Page={EnrollmentConfirmation} /> },
        { path: "/:lang/confirmacao-preinscricao", element: <PageWithSuspense Page={EnrollmentConfirmation} /> },
        { path: "/:lang/confirmation-preinscription", element: <PageWithSuspense Page={EnrollmentConfirmation} /> },

        // Renewal - Renovación de Matrícula
        { path: "/:lang/renovacion", element: <PageWithSuspense Page={Renewal} /> },
        { path: "/:lang/renewal", element: <PageWithSuspense Page={Renewal} /> },
        { path: "/:lang/renovacao", element: <PageWithSuspense Page={Renewal} /> },
        { path: "/:lang/renouvellement", element: <PageWithSuspense Page={Renewal} /> },

        // Renewal Confirmation
        { path: "/:lang/confirmacion-renovacion", element: <PageWithSuspense Page={RenewalConfirmation} /> },
        { path: "/:lang/renewal-confirmation", element: <PageWithSuspense Page={RenewalConfirmation} /> },
        { path: "/:lang/confirmacao-renovacao", element: <PageWithSuspense Page={RenewalConfirmation} /> },
        { path: "/:lang/confirmation-renouvellement", element: <PageWithSuspense Page={RenewalConfirmation} /> },

        // Política de privacidad (slugs en src/lib/legalPaths.ts)
        { path: "/:lang/politica-de-privacidad", element: <PageWithSuspense Page={PrivacyPolicy} /> },
        { path: "/:lang/privacy-policy", element: <PageWithSuspense Page={PrivacyPolicy} /> },
        { path: "/:lang/politica-de-privacidade", element: <PageWithSuspense Page={PrivacyPolicy} /> },
        { path: "/:lang/politique-de-confidentialite", element: <PageWithSuspense Page={PrivacyPolicy} /> },

        // Condiciones de contratación de campamentos y cumpleaños (slugs en src/lib/legalPaths.ts)
        { path: "/:lang/condiciones-de-contratacion", element: <PageWithSuspense Page={TermsConditions} /> },
        { path: "/:lang/terms-and-conditions", element: <PageWithSuspense Page={TermsConditions} /> },
        { path: "/:lang/condicoes-de-contratacao", element: <PageWithSuspense Page={TermsConditions} /> },
        { path: "/:lang/conditions-generales", element: <PageWithSuspense Page={TermsConditions} /> },

        // Birthdays
        { path: "/:lang/cumpleanos", element: <PageWithSuspense Page={Birthdays} /> },
        { path: "/:lang/birthdays", element: <PageWithSuspense Page={Birthdays} /> },
        { path: "/:lang/aniversarios", element: <PageWithSuspense Page={Birthdays} /> },
        { path: "/:lang/anniversaires", element: <PageWithSuspense Page={Birthdays} /> },
        
        // Easter Camp
        { path: "/:lang/campamento-semana-santa", element: <PageWithSuspense Page={EasterCamp} /> },
        { path: "/:lang/easter-camp", element: <PageWithSuspense Page={EasterCamp} /> },
        { path: "/:lang/acampamento-pascoa", element: <PageWithSuspense Page={EasterCamp} /> },
        { path: "/:lang/camp-paques", element: <PageWithSuspense Page={EasterCamp} /> },
        
        // Summer Camp
        { path: "/:lang/campamento-verano", element: <PageWithSuspense Page={SummerCamp} /> },
        { path: "/:lang/summer-camp", element: <PageWithSuspense Page={SummerCamp} /> },
        { path: "/:lang/acampamento-verao", element: <PageWithSuspense Page={SummerCamp} /> },
        { path: "/:lang/camp-ete", element: <PageWithSuspense Page={SummerCamp} /> },

        // Summer Camp Confirmation
        { path: "/:lang/confirmacion-campamento-verano", element: <PageWithSuspense Page={SummerCampConfirmation} /> },
        { path: "/:lang/summer-camp-confirmation", element: <PageWithSuspense Page={SummerCampConfirmation} /> },
        { path: "/:lang/confirmacao-acampamento-verao", element: <PageWithSuspense Page={SummerCampConfirmation} /> },
        { path: "/:lang/confirmation-camp-ete", element: <PageWithSuspense Page={SummerCampConfirmation} /> },

        // Christmas Camp
        { path: "/:lang/campamento-navidad", element: <PageWithSuspense Page={ChristmasCamp} /> },
        { path: "/:lang/christmas-camp", element: <PageWithSuspense Page={ChristmasCamp} /> },
        { path: "/:lang/acampamento-natal", element: <PageWithSuspense Page={ChristmasCamp} /> },
        { path: "/:lang/camp-noel", element: <PageWithSuspense Page={ChristmasCamp} /> },
        
        // Courses
        { path: "/:lang/cursos", element: <PageWithSuspense Page={Courses} /> },
        { path: "/:lang/courses", element: <PageWithSuspense Page={Courses} /> },
        { path: "/:lang/cours", element: <PageWithSuspense Page={Courses} /> },
        
        // Feedback
        { path: "/:lang/quejas-sugerencias", element: <PageWithSuspense Page={Feedback} /> },
        { path: "/:lang/feedback", element: <PageWithSuspense Page={Feedback} /> },
        { path: "/:lang/reclamacoes-sugestoes", element: <PageWithSuspense Page={Feedback} /> },
        { path: "/:lang/plaintes-suggestions", element: <PageWithSuspense Page={Feedback} /> },
        
        { path: "*", element: <PageWithSuspense Page={NotFound} /> },
      ],
    },
  ]);

  return (
    <LanguageProvider>
      <RouterProvider router={router} />
    </LanguageProvider>
  );
}

export default App;
